import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AuthLayout } from "@/components/app/AuthLayout";
import { TextField, PrimaryButton } from "@/components/app/TextField";
import { GoogleButton } from "@/components/app/GoogleButton";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { AUTH } from "@/constants/testIds";

function safeReturnTo(value) {
  if (!value) return "/biblioteka";
  if (!value.startsWith("/") || value.startsWith("//")) return "/biblioteka";
  return value;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnTo = safeReturnTo(params.get("returnTo"));

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Zalogowano pomyślnie.");
      navigate(returnTo, { replace: true });
    } catch (err) {
      setError(apiError(err.response?.data?.detail, err.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout
      title="Zaloguj się"
      subtitle="Wejdź do swojej biblioteki promptów Promptivo."
      footer={
        <span>
          Nie masz konta?{" "}
          <Link
            to="/rejestracja"
            data-testid={AUTH.registerLink}
            className="font-medium text-brand-accent hover:underline"
          >
            Załóż konto
          </Link>
        </span>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        {error && (
          <div
            data-testid={AUTH.error}
            className="rounded-field border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          >
            {error}
          </div>
        )}
        <TextField
          label="E-mail"
          type="email"
          autoComplete="email"
          testId={AUTH.emailInput}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="ty@firma.pl"
          required
        />
        <TextField
          label="Hasło"
          type="password"
          autoComplete="current-password"
          testId={AUTH.passwordInput}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••"
          required
        />
        <div className="flex justify-end">
          <Link
            to="/przypomnij-haslo"
            data-testid={AUTH.forgotPasswordLink}
            className="text-sm text-brand-sec hover:text-brand-text"
          >
            Nie pamiętam hasła
          </Link>
        </div>
        <PrimaryButton type="submit" data-testid={AUTH.submitButton} disabled={loading}>
          {loading ? "Logowanie…" : "Zaloguj się"}
        </PrimaryButton>
      </form>
      <div className="my-6 flex items-center gap-4">
        <div className="h-px flex-1 bg-brand-line" />
        <span className="text-xs uppercase tracking-wide text-brand-muted">lub</span>
        <div className="h-px flex-1 bg-brand-line" />
      </div>
      <GoogleButton testId={AUTH.googleButton} label="Zaloguj się przez Google" />
    </AuthLayout>
  );
}
